import React from "react"
import { useState, useEffect } from "react"
import type { CSSProperties, ReactNode } from "react"
import { Helmet } from "react-helmet"

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=DM+Mono:wght@300;400&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :root {
    --sand: #f5f0e8;
    --sand-dark: #e8e0d0;
    --ink: #2a2520;
    --ink-light: #6b5f52;
    --accent: #c4744a;
    --accent-light: #e8c4a8;
    --warm-white: #faf7f2;
  }

  html { scroll-behavior: smooth; }

  a {
    color: inherit;
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    text-decoration: underline;
  }

  body {
    background-color: var(--sand);
    color: var(--ink);
    font-family: 'Lora', Georgia, serif;
    min-height: 100vh;
    overflow-x: hidden;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .animate-fade-up   { animation: fadeUp 0.7s ease forwards; }
  .animate-fade-in   { animation: fadeIn 1s ease forwards; }
  .opacity-0         { opacity: 0; }

  .grain {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 100;
    opacity: 0.035;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 200px 200px;
  }
`

type HoverStyleFn = (hovered: boolean) => CSSProperties

const s: {
  page: CSSProperties
  nav: CSSProperties
  navMono: CSSProperties
  navDot: CSSProperties
  main: CSSProperties
  heroEyebrow: CSSProperties
  heroTitle: CSSProperties
  heroTitleEm: CSSProperties
  heroSub: CSSProperties
  divider: CSSProperties
  content: CSSProperties
  blockLabel: CSSProperties
  blockLegalRef: CSSProperties
  blockText: CSSProperties
  accentLink: CSSProperties
  footer: CSSProperties
  footerMono: CSSProperties
  blockGrid: (isMobile: boolean) => CSSProperties
  contactLink: HoverStyleFn
} = {
  page: {
    backgroundColor: "var(--sand)",
    color: "var(--ink)",
    fontFamily: "'Lora', Georgia, serif",
    minHeight: "100vh",
    overflowX: "hidden",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2.2rem 2rem",
    maxWidth: 900,
    margin: "0 auto",
    position: "relative",
  },
  navMono: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    fontWeight: 300,
    letterSpacing: "0.12em",
    color: "var(--ink-light)",
    textTransform: "uppercase",
  },
  navDot: {
    width: 6,
    height: 6,
    background: "var(--accent)",
    borderRadius: "50%",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  main: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "0 2rem",
  },
  heroEyebrow: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    fontWeight: 300,
    letterSpacing: "0.2em",
    color: "var(--accent)",
    textTransform: "uppercase",
    marginBottom: "1.4rem",
  },
  heroTitle: {
    fontSize: "clamp(3rem, 7vw, 5.5rem)",
    fontWeight: 400,
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
    color: "var(--ink)",
    marginBottom: "0.3rem",
  },
  heroTitleEm: {
    fontStyle: "italic",
    color: "var(--accent)",
  },
  heroSub: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.8rem",
    fontWeight: 300,
    letterSpacing: "0.08em",
    color: "var(--ink-light)",
    marginTop: "1rem",
  },
  divider: {
    height: 1,
    background: "linear-gradient(to right, var(--accent-light), transparent)",
    margin: "3rem 0",
  },
  content: {
    paddingBottom: "6rem",
  },
  blockLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    fontWeight: 300,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "var(--ink-light)",
    marginBottom: "0.5rem",
  },
  blockLegalRef: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.72rem",
    fontWeight: 300,
    color: "var(--accent-light)",
    letterSpacing: "0.04em",
  },
  blockText: {
    fontSize: "1rem",
    lineHeight: 1.8,
    color: "var(--ink)",
  },
  accentLink: {
    color: "var(--accent)",
    textDecoration: "none",
  },
  footer: {
    borderTop: "1px solid var(--sand-dark)",
    padding: "1.8rem 2rem",
    maxWidth: 900,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
  },
  footerMono: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    fontWeight: 300,
    color: "var(--ink-light)",
    letterSpacing: "0.1em",
  },
  blockGrid: (isMobile: boolean): CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "160px 1fr",
    gap: isMobile ? "0.4rem" : "3rem",
    alignItems: "start",
    marginBottom: "2.8rem",
  }),
  contactLink: (hovered: boolean): CSSProperties => ({
    color: hovered ? "var(--accent)" : "var(--ink-light)",
    transition: "color 0.2s",
  }),
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  )

  useEffect(() => {
    const handler = (): void => setIsMobile(window.innerWidth < 640)
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])

  return isMobile
}

type BlockProps = {
  label: string
  legalRef: string
  isMobile: boolean
  children: ReactNode
}

function Block({
  label,
  legalRef,
  isMobile,
  children,
}: BlockProps): JSX.Element {
  return (
    <div style={s.blockGrid(isMobile)}>
      <div>
        <p style={s.blockLabel}>{label}</p>
        <p style={s.blockLegalRef}>{legalRef}</p>
      </div>
      <div style={s.blockText}>{children}</div>
    </div>
  )
}

type NavLinkProps = {
  href: string
  children: ReactNode
}

function NavLink({ href, children }: NavLinkProps): JSX.Element {
  const [hovered, setHovered] = useState<boolean>(false)

  return (
    <a
      href={href}
      style={s.contactLink(hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  )
}

export default function ImprintPage(): JSX.Element {
  const isMobile = useIsMobile()

  const delays: string[] = ["0.1s", "0.2s", "0.35s", "0.5s", "0.6s"]

  return (
    <div style={s.page}>
      <Helmet>
        <title>Hendrik Gruber | Imprint</title>
        <meta
          name="description"
          content="Imprint information for Hendrik Gruber's website."
        />
      </Helmet>
      <style>{styles}</style>

      <div className="grain" />

      <nav style={s.nav}>
        <span style={s.navMono}>
          <NavLink href="/">Home</NavLink>
        </span>
        <div style={s.navDot} />
        <span style={s.navMono}>Imprint</span>
      </nav>

      <main style={s.main}>
        <section
          style={{ padding: isMobile ? "3rem 0 2.5rem" : "5rem 0 4rem" }}
        >
          <p
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroEyebrow, animationDelay: delays[0] }}
          >
            Information pursuant to Section 5 DDG
          </p>
          <h1
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroTitle, animationDelay: delays[1] }}
          >
            Legal <em style={s.heroTitleEm}>Notice.</em>
          </h1>
          <p
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroSub, animationDelay: delays[2] }}
          >
            Imprint · Last updated: {new Date().getFullYear()}
          </p>
        </section>

        <div
          className="opacity-0 animate-fade-in"
          style={{ ...s.divider, animationDelay: delays[3] }}
        />

        <div
          className="opacity-0 animate-fade-up"
          style={{ ...s.content, animationDelay: delays[4] }}
        >
          <Block label="Provider" legalRef="Section 5 DDG" isMobile={isMobile}>
            <p>Hendrik Gruber</p>
          </Block>

          <Block
            label="Address"
            legalRef="Section 5 (1) No. 1 DDG"
            isMobile={isMobile}
          >
            <p>Sindlinger Str. 16</p>
            <p>60326 Frankfurt am Main</p>
            <p>Germany</p>
          </Block>

          <Block
            label="Contact"
            legalRef="Section 5 (1) No. 2 DDG"
            isMobile={isMobile}
          >
            <p>
              Email:{" "}
              <a href="mailto:gruber.hendrik@gmail.com" style={s.accentLink}>
                gruber.hendrik@gmail.com
              </a>
            </p>
          </Block>

          <Block
            label="Responsible for content"
            legalRef="Section 18 (2) MStV"
            isMobile={isMobile}
          >
            <p>Hendrik Gruber</p>
            <p>Sindlinger Str. 16</p>
            <p>60326 Frankfurt am Main</p>
            <p>Germany</p>
          </Block>
        </div>
      </main>

      <footer style={s.footer}>
        <span style={s.footerMono}>
          © {new Date().getFullYear()} Hendrik Gruber
        </span>
        <span style={s.footerMono}>Frankfurt am Main, DE</span>
        <a href="/privacy" style={s.footerMono}>
          Privacy Policy
        </a>
      </footer>
    </div>
  )
}
